import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {useState} from "react";
import {useTranslation} from "react-i18next";

import {selectByLanguage} from "@reva/shared/i18n";
import type {ValueOrderDocument} from "@reva/shared/value-order";

import type {FailedValuationDocument, FinalizeValuationResponse} from "../../api/payload-types/persistence-finalize";
import {useLanguage} from "../../hooks/languageHooks";
import {downloadDocumentAsync} from "../../redux/documentsThunk";
import {useRevaDispatch} from "../../redux/revaHooks";
import type {RevaDispatch} from "../../redux/store";
import {openBlobForDownload} from "../../util/browserUtil";
import {useAllowedArchiveDocumentTypes} from "./ArchiveDocumentsSelection";

/** Fetch one archived document as Blob and hand it to the browser as a download. */
async function downloadAndOpenDocument(dispatch: RevaDispatch, document: ValueOrderDocument) {
    const blob = (await dispatch(downloadDocumentAsync(document))) as Blob | null;
    openBlobForDownload(blob, document.filename);
}

/** What the (pre-)finalize dialogs have to show for the documents of a finalize response. */
export interface FinalizeDocumentsOutcome {
    /** Documents that were archived, offered for download. */
    archived: ValueOrderDocument[];
    /** Documents that were generated but not archived. */
    failed: FailedValuationDocument[];
    /** True when a single archived document can be downloaded right away, without opening the dialog. */
    autoDownload: boolean;
}

/**
 * Decides what to do with the documents of a (pre-)finalization response. The direct download of a single
 * document is only taken when nothing failed — otherwise the dialog has to open, so that the "some
 * documents are missing" notice cannot be swallowed. That matters most in the worst case, where every
 * upload failed and there is no archived document at all.
 */
export function resolveFinalizeDocuments(response: FinalizeValuationResponse): FinalizeDocumentsOutcome {
    const archived = response.archivedDocuments ?? [];
    const failed = response.failedDocuments ?? [];
    return {archived, failed, autoDownload: failed.length === 0 && archived.length === 1};
}

/**
 * Shared success handling for the documents generated by a (pre-)finalization
 * (FinalizeValuationResponse.archivedDocuments / .failedDocuments), used by
 * DialogPreFinalizeValuation and DialogFinalizeValuation:
 *  - exactly 1 archived document and nothing failed: the PDF is downloaded and opened right away
 *  - anything else with at least one document: DialogArchivedDocuments offers the archived ones for
 *    download and — for the ones that could not be archived — names them with their reason
 *  - no documents at all: nothing happens (the calling dialog just closes)
 *
 * Download errors must NOT mask the successful finalization — they are only logged,
 * the calling dialog closes regardless.
 *
 * The returned `archivedDocumentsDialog` must be rendered by the calling dialog
 * component even while that dialog itself is closed (it opens right AFTER closing).
 */
export function useArchivedDocumentsHandler() {
    const dispatch = useRevaDispatch();
    const [outcome, setOutcome] = useState<FinalizeDocumentsOutcome | null>(null);

    const handleArchivedDocuments = (response: FinalizeValuationResponse) => {
        const resolved = resolveFinalizeDocuments(response);
        if (resolved.autoDownload) {
            // Fire and forget: the calling dialog closes immediately.
            downloadAndOpenDocument(dispatch, resolved.archived[0]).catch((err: unknown) =>
                console.error("Download of the archived document failed", err),
            );
        } else if (resolved.archived.length > 0 || resolved.failed.length > 0) {
            setOutcome(resolved);
        }
    };

    const closeDialog = () => setOutcome(null);
    const archivedDocumentsDialog = outcome ? (
        <DialogArchivedDocuments documents={outcome.archived} failed={outcome.failed} onClose={closeDialog} />
    ) : null;

    return {handleArchivedDocuments, archivedDocumentsDialog};
}

/**
 * Warning for the documents that were generated during the (pre-)finalization but could not be archived
 * on the Orders-API. The (pre-)finalization itself went through, so this is a "missing, can be created
 * later" notice: every document type stays retrievable from the frozen snapshot via the snapshot submenu
 * of the lifecycle menu, which archives it on the way.
 */
function FailedDocumentsWarning({failed}: {failed: FailedValuationDocument[]}) {
    const {t} = useTranslation();
    const language = useLanguage();
    const allowedDocTypes = useAllowedArchiveDocumentTypes();

    // Label from the same permission→docPath mapping the selection checkboxes use; the raw docPath is the
    // fallback, should a type be missing from the permission-filtered list.
    const docTypeLabel = (documentType: string) => {
        const option = allowedDocTypes.find((allowed) => allowed.docPath === documentType);
        return option ? t(option.labelKey) : documentType;
    };

    return (
        <Message
            id="failedDocumentsWarning"
            severity="warn"
            className="block w-full leading-normal"
            content={
                <div className="flex items-start gap-2">
                    <i className={`${PrimeIcons.EXCLAMATION_TRIANGLE} mt-1`} />
                    <div className="flex flex-col gap-2">
                        <span>{t("value.actions.failedDocumentsWarning", {count: failed.length})}</span>
                        {/* No bullets: the bold document type already separates the entries, and the
                            reason below it needs the full width. Padding/marker reset by hand, because
                            Tailwind's Preflight is switched off (see tailwind.config.js). */}
                        <ul className="m-0 p-0 list-none flex flex-col gap-3">
                            {failed.map((document) => (
                                <li key={document.documentType}>
                                    <div className="font-bold">{docTypeLabel(document.documentType)}:</div>
                                    {/* break-words: the reason is passed through from the Orders-API and
                                        may contain a long unbroken token (filename, URL). */}
                                    <div className="break-words">
                                        {t("value.actions.failedDocumentReason", {
                                            reason: selectByLanguage(language, document.errorDe, document.errorEn),
                                        })}
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <span>{t("value.actions.failedDocumentsHint", {count: failed.length})}</span>
                    </div>
                </div>
            }
        />
    );
}

interface Props {
    /** The archived documents to offer for download (one row each); may be empty */
    documents: ValueOrderDocument[];
    /** Documents that were generated but could not be archived; may be empty */
    failed: FailedValuationDocument[];
    /** Called when the dialog is closed */
    onClose: () => void;
}

/**
 * Dialog for the documents of a (pre-)finalization — shown whenever they cannot be handled silently: more
 * than one document was generated, or a document could not be archived. With exactly one archived
 * document and no failure the PDF is opened directly instead (see useArchivedDocumentsHandler).
 */
function DialogArchivedDocuments({documents, failed, onClose}: Props) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();
    // documentId of the document currently being downloaded (or null)
    const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(null);

    const onDownload = async (document: ValueOrderDocument) => {
        setLoadingDocumentId(document.documentId);
        try {
            await downloadAndOpenDocument(dispatch, document);
        } catch (err) {
            console.error("Download of the archived document failed", err);
        } finally {
            setLoadingDocumentId(null);
        }
    };

    const footer = (
        <Button
            id="archivedDocumentsCloseButton"
            label={t("general.close")}
            icon={PrimeIcons.TIMES}
            text
            onClick={onClose}
        />
    );

    const hasFailed = failed.length > 0;
    // Three outcomes, three headers: nothing failed, NOTHING was archived, or a mix. "Not all documents"
    // reads confusingly when there was no successful upload at all — the most likely case, since the
    // uploads of one (pre-)finalization usually fail for the same reason.
    const headerKey = !hasFailed
        ? "value.actions.archivedDocumentsTitle"
        : documents.length === 0
          ? "value.actions.archivedDocumentsFailedTitle"
          : "value.actions.archivedDocumentsPartialTitle";

    return (
        <Dialog
            id="archivedDocumentsDialog"
            // count drives the singular/plural of the "nothing archived" header; the other two keys
            // have no plural forms and ignore it.
            header={t(headerKey, {count: failed.length})}
            style={{minWidth: "30rem", maxWidth: "min(90vw, 80rem)"}}
            visible
            onHide={onClose}
            footer={footer}
        >
            <div className="flex flex-col gap-4">
                {hasFailed && <FailedDocumentsWarning failed={failed} />}
                {documents.length > 0 && (
                    <div className="flex flex-col gap-2 items-start">
                        {/* Only worth a heading when the failure warning above it needs to be told apart. */}
                        {hasFailed && <span className="font-bold">{t("value.actions.archivedDocumentsSection")}</span>}
                        {documents.map((document) => (
                            <Button
                                key={document.documentId}
                                label={document.filename}
                                icon={PrimeIcons.DOWNLOAD}
                                loading={loadingDocumentId === document.documentId}
                                disabled={loadingDocumentId !== null}
                                onClick={() => void onDownload(document)}
                                text
                            />
                        ))}
                    </div>
                )}
            </div>
        </Dialog>
    );
}
